import React from 'react';

import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Pagination, PaginationItem } from '@tmap/mmm-style-guide/src/Pagination';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { useWidth } from '@tmap/mmm-style-guide/src/useWidth';

import Link from 'next/link';
import Metatags from '../../components/metatags';
import NewsCard from '../../components/newsCard';
import FullWrapper from '../../layouts/fullWrapper';

import client from '../../client';
import setCacheHeaders from '../../lib/setCacheHeaders';

const sectionSpacing = ({ theme }) => ({
  paddingLeft: theme.spacing(10),
  paddingRight: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
});

const HeaderWrapper = styled(Grid)(({ theme }) => ({
  backgroundColor: 'rgba(200,193,187,0.15)',
  paddingTop: '56px',
  paddingBottom: '90px',
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  ...sectionSpacing({ theme }),
}));

const SectionSpacer = styled(Grid)(sectionSpacing);

const ArticlesWrapper = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  ...sectionSpacing({ theme }),
}));

const PagerWrapper = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(5),
}));

function NewsIndex(props) {
  const {
    articles = [],
    total = 0,
    page,
  } = props;
  const width = useWidth();
  const totalPages = Math.trunc((total / 18) + 1);
  const noColumns = {
    xs: 1,
    sm: 2,
  }[width] || 3;
  const columns = [[], [], []];

  articles.forEach((v, i) => {
    columns[i % noColumns].push(
      <Grid item xs={12} sx={{ marginBottom: '24px' }} key={`news-item-${i}`}>
        <NewsCard article={v} shouldLazyLoad={i >= noColumns} />
      </Grid>,
    );
  });

  const canonicalUrl = `${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/news`;

  return (
    <>
      <Metatags
        title='In The News'
        description='MakeMyMove In The News'
        canonical={canonicalUrl}
      />
      <FullWrapper>
        <Grid container justifyContent='center'>
          <HeaderWrapper item xs={12}>
            <Typography variant='body2' color='secondary' gutterBottom>PRESS & NEWS</Typography>
            <Typography variant='h3' color='dark' component='h1' fontFamily='serif' fontWeight={700}>MakeMyMove In The News</Typography>
          </HeaderWrapper>
          <SectionSpacer item xs={12}>
            <Typography variant='h4' color='dark' fontWeight={700}>
              Recent Coverage
            </Typography>
          </SectionSpacer>
          <ArticlesWrapper item xs={12}>
            <Grid container spacing={3}>
              {columns.map((col, i) => (i < noColumns ? (
                <Grid item xs={12} sm={6} md={4} key={`content-column-${i}`}>
                  <Grid container>
                    {col}
                  </Grid>
                </Grid>
              ) : null
              ))}
            </Grid>
          </ArticlesWrapper>
          <PagerWrapper item>
            <Pagination
              count={totalPages}
              page={page}
              hidePrevButton={page === 1}
              hideNextButton={page >= totalPages}
              renderItem={(item) => (
                <Link href={`/news?page=${item.page}`}><PaginationItem {...item} /></Link>
              )}
            />
          </PagerWrapper>
        </Grid>
      </FullWrapper>
    </>
  );
}

export const getServerSideProps = async (context) => {
  const page = context.query.page ? parseInt(context.query.page, 10) : 1;

  const pagingRange = `[${((page - 1) * 18)}...${(page * 18)}]`;
  const currentPageQuery = `*[_type == "news"]  | order(_createdAt desc) ${pagingRange}`;
  const countQuery = 'count(*[_type == "news"])';
  const result = await client.fetch(`{
        "articles":${currentPageQuery},
        "total":${countQuery}  
    }`);

  if (!result) {
    return { notFound: true };
  }
  setCacheHeaders(context.res, 300, 3600);
  return { props: { ...result, page } };
};

export default NewsIndex;
